import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { deleteCountryImage, getCountries } from '../../store/actions/country';
import config from '../../config';

// Helper to resolve image URL
export const resolveImageUrl = (path) => {
  if (!path) return '';

  // 1. Handle replacement of production hostname for local development
  if (path.startsWith('http')) {
    const oldBase = "https://adminapi.zeronine.ai";
    const newBase = config.API_URL.replace(/\/api\/?$/, '');

    if (path.startsWith(oldBase) && newBase.includes('192.168')) {
      // Replace production domain with local IP
      return path.replace(oldBase, newBase);
    }
    // If it's already a full URL (including replaced ones), just return it
    return path;
  }

  // 2. Handle relative paths
  const baseUrl = config.API_URL.replace(/\/api\/?$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const CountryForm = ({ visible, onCreate, onCancel, record }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [hasExistingImage, setHasExistingImage] = useState(false);

  useEffect(() => {
    if (visible) {
      if (record) {
        const hasImage = !!record.imagepath;
        setHasExistingImage(hasImage);

        const initialUrl = resolveImageUrl(record.imagepath);

        const initialValues = {
          ...record,
          image: hasImage ? [{
            uid: '-1',
            name: 'existing_image',
            status: 'done',
            url: initialUrl,
            thumbUrl: initialUrl,
          }] : [],
        };
        form.setFieldsValue(initialValues);
      } else {
        setHasExistingImage(false);
        form.resetFields();
      }
    }
  }, [record, form, visible]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList || [];
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isPNG = file.type === 'image/png';
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg';

      if (!isPNG && !isJPG) {
        message.error(`${file.name} is not a valid image format (PNG/JPG only)`);
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    onRemove: (file) => {
      if (file.uid === '-1' && record?.id) {
        return new Promise((resolve) => {
          Swal.fire({
            title: 'Delete Image?',
            text: 'Are you sure you want to delete this image? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
          }).then(async (result) => {
            if (result.isConfirmed) {
              try {
                const success = await dispatch(deleteCountryImage(record.id));
                if (success) {
                  setHasExistingImage(false);
                  await dispatch(getCountries());
                  resolve(true);
                } else {
                  resolve(false);
                }
              } catch (error) {
                resolve(false);
              }
            } else {
              resolve(false);
            }
          });
        });
      }
      return true;
    },
    onChange: (info) => {
      if (info.fileList.length > 0 && info.fileList[0].originFileObj) {
        setHasExistingImage(false);
      }
    },
    maxCount: 1,
    listType: "picture",
    accept: ".png,.jpg,.jpeg"
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onCreate(values);
    } catch (error) {
      console.warn('Form validation failed:', error);
    }
  };

  return (
    <Modal
      visible={visible}
      title={record ? "Update Country" : "Add Country"}
      okText={record ? "Update" : "Submit"}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={handleOk}
      width={600}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        name="country_form"
        preserve={false}
      >
        <Form.Item
          name="name"
          label="Country Name"
          rules={[{ required: true, message: 'Please enter the country name' }]}
        >
          <Input placeholder="e.g., United States" />
        </Form.Item>

        <Form.Item
          name="code"
          label="Country Code"
          rules={[{ required: true, message: 'Please enter the country code' }]}
        >
          <Input placeholder="e.g., US" />
        </Form.Item>

        <Form.Item
          name="nationality"
          label="Nationality"
          rules={[{ required: true, message: 'Please enter the nationality' }]}
        >
          <Input placeholder="e.g., American" />
        </Form.Item>

        <Form.Item
          name="image"
          label="Country Flag / Image"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          extra="Supported formats: PNG, JPG, JPEG (Max 1 file)"
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CountryForm;